<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <div>
        <router-link
          :to="{ name: 'ModeratorsList' }"
          class="text-blue-600 hover:text-blue-800"
        >
          <span class="text-sm">&larr; {{ $t("back") }}</span>
        </router-link>
        <h1 class="text-2xl font-bold text-gray-800 mt-2">
          {{ $t("moderator_statistics") }}
        </h1>
      </div>

      <div class="flex gap-2">
        <template v-if="isAdmin && moderator">
          <BaseButton
            :type="moderator.is_active ? 'danger' : 'primary'"
            @click="toggleModeratorActive"
          >
            {{
              $t(moderator.is_active ? "block_moderator" : "unblock_moderator")
            }}
          </BaseButton>
          <BaseButton type="secondary" @click="changeModeratorPassword">
            {{ $t("change_password") }}
          </BaseButton>
          <BaseButton type="primary" @click="openLeadReassignment">
            {{ $t("reassign_leads") }}
          </BaseButton>
          <BaseButton type="primary" @click="openUserReassignment">
            {{ $t("reassign_users") }}
          </BaseButton>
          <BaseButton type="danger" @click="deleteModerator">
            {{ $t("delete_moderator") }}
          </BaseButton>
        </template>
        <BaseButton type="primary" @click="fetchData">
          {{ $t("refresh_data") }}
        </BaseButton>
      </div>
    </div>

    <div
      v-if="statistics"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
    >
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("cars_created") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ statistics.cars_created }}
        </p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("cars_updated") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ statistics.cars_updated }}
        </p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("cars_deleted") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ statistics.cars_deleted }}
        </p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("cars_approved") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ statistics.cars_approved }}
        </p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("cars_rejected") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ statistics.cars_rejected }}
        </p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("users_created") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ statistics.users_created }}
        </p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("users_updated") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ statistics.users_updated }}
        </p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("assigned_users_count") }}</p>
        <p class="text-2xl font-bold text-gray-800">{{ assignedUsersCount }}</p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("login_count") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ statistics.login_count }}
        </p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("logout_count") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ statistics.logout_count }}
        </p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("total_activities") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ statistics.total_activities }}
        </p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("first_activity") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ formatDate(statistics.first_activity) }}
        </p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow-sm">
        <p class="text-sm text-gray-500">{{ $t("last_activity") }}</p>
        <p class="text-2xl font-bold text-gray-800">
          {{ formatDate(statistics.last_activity) }}
        </p>
      </div>
    </div>

    <div class="bg-white p-4 rounded-lg shadow-sm">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold">{{ $t("recent_activity") }}</h2>
        <BaseButton type="secondary" @click="toggleRecentActivity">
          {{ $t(isRecentActivityVisible ? "hide" : "show") }}
        </BaseButton>
      </div>

      <div v-if="isRecentActivityVisible">
        <div class="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t("action") }}
            </label>
            <select
              v-model="filters.action"
              @change="fetchActivities"
              class="w-full border rounded-md p-2"
            >
              <option value="">
                {{ $t("all") }}
              </option>
              <option
                v-for="action in actions"
                :key="action.value"
                :value="action.value"
              >
                {{ action.label }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t("period") }}
            </label>
            <select
              v-model="filters.period"
              @change="periodChanged"
              class="w-full border rounded-md p-2"
            >
              <option value="">
                {{ $t("all") }}
              </option>
              <option value="today">
                {{ $t("today") }}
              </option>
              <option value="yesterday">
                {{ $t("yesterday") }}
              </option>
              <option value="this_week">
                {{ $t("this_week") }}
              </option>
              <option value="last_week">
                {{ $t("last_week") }}
              </option>
              <option value="this_month">
                {{ $t("this_month") }}
              </option>
              <option value="last_month">
                {{ $t("last_month") }}
              </option>
              <option value="last_7_days">
                {{ $t("last_7_days") }}
              </option>
              <option value="last_30_days">
                {{ $t("last_30_days") }}
              </option>
              <option value="custom">
                {{ $t("custom") }}
              </option>
            </select>
          </div>
        </div>

        <div
          v-if="filters.period === 'custom'"
          class="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t("from") }}
            </label>
            <input
              type="datetime-local"
              v-model="filters.from"
              @change="fetchActivities"
              class="w-full border rounded-md p-2"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t("to") }}
            </label>
            <input
              type="datetime-local"
              v-model="filters.to"
              @change="fetchActivities"
              class="w-full border rounded-md p-2"
            />
          </div>
        </div>

        <BaseDataTable :columns="columns" :data="activities" :loading="loading">
          <template #action="{ row }">
            <span
              class="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700"
            >
              {{ formatAction(row.action) }}
            </span>
          </template>
          <template #subject="{ row }">
            {{ formatSubject(row.subject_type) }}
          </template>
          <template #context="{ row }">
            <span class="truncate block max-w-xs">
              {{ contextPreview(row.context) }}
            </span>
          </template>
          <template #actions="{ row }">
            <button
              @click="openDetails(row)"
              class="text-blue-600 hover:text-blue-800 font-medium"
            >
              {{ $t("view") }}
            </button>
          </template>
        </BaseDataTable>

        <BasePagination
          v-if="pagination"
          :pagination="pagination"
          @change-page="handlePageChange"
        />
      </div>
    </div>

    <div
      v-if="showLeadReassignment"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="closeLeadReassignment"
    >
      <div
        class="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-dark-surface"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-xl font-bold text-gray-900 dark:text-white">
              {{ $t("reassign_moderator_leads") }}
            </h2>
            <p class="mt-1 text-sm text-gray-500 dark:text-slate-400">
              {{ $t("lead_reassignment_description") }}
            </p>
          </div>
          <button
            class="text-2xl text-gray-400 hover:text-gray-700"
            :aria-label="$t('close')"
            @click="closeLeadReassignment"
          >
            ×
          </button>
        </div>

        <div
          v-if="leadReassignmentLoading"
          class="py-12 text-center text-gray-500"
        >
          {{ $t("loading") }}
        </div>

        <template v-else-if="leadReassignmentPreview">
          <div class="mt-6 grid gap-4 sm:grid-cols-2">
            <div class="rounded-lg bg-blue-50 p-4 dark:bg-blue-950/30">
              <p class="text-sm font-medium text-blue-700 dark:text-blue-300">
                {{ $t("leads_to_reassign") }}
              </p>
              <p
                class="mt-1 text-3xl font-bold text-blue-900 dark:text-blue-100"
              >
                {{ leadReassignmentPreview.lead_count }}
              </p>
              <p class="mt-1 text-sm text-blue-700 dark:text-blue-300">
                {{ leadReassignmentPreview.source.name }}
              </p>
            </div>
            <div class="rounded-lg bg-emerald-50 p-4 dark:bg-emerald-950/30">
              <p
                class="text-sm font-medium text-emerald-700 dark:text-emerald-300"
              >
                {{ $t("available_moderators") }}
              </p>
              <p
                class="mt-1 text-3xl font-bold text-emerald-900 dark:text-emerald-100"
              >
                {{ leadReassignmentPreview.allocations.length }}
              </p>
              <p class="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
                {{ $t("balanced_by_current_load") }}
              </p>
            </div>
          </div>

          <div class="mt-6 grid gap-6 lg:grid-cols-2">
            <section>
              <h3 class="font-semibold text-gray-900 dark:text-white">
                {{ $t("moderator_leads") }}
              </h3>
              <div
                class="mt-3 max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-slate-700"
              >
                <div
                  v-for="lead in leadReassignmentPreview.leads"
                  :key="lead.id"
                  class="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-slate-800"
                >
                  <div>
                    <p class="font-medium text-gray-900 dark:text-white">
                      {{ lead.name }}
                    </p>
                    <p class="text-xs text-gray-500">{{ lead.phone || "—" }}</p>
                  </div>
                  <span
                    class="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-dark-input dark:text-slate-300"
                  >
                    {{ formatLeadStatus(lead.status) }}
                  </span>
                </div>
                <p
                  v-if="leadReassignmentPreview.leads.length === 0"
                  class="p-4 text-sm text-gray-500"
                >
                  {{ $t("no_leads_to_reassign") }}
                </p>
              </div>
            </section>

            <section>
              <h3 class="font-semibold text-gray-900 dark:text-white">
                {{ $t("distribution_preview") }}
              </h3>
              <div class="mt-3 space-y-3">
                <div
                  v-for="allocation in leadReassignmentPreview.allocations"
                  :key="allocation.profile_id"
                  class="rounded-lg border border-gray-200 p-4 dark:border-slate-700"
                >
                  <div class="flex items-center justify-between gap-3">
                    <p class="font-semibold text-gray-900 dark:text-white">
                      {{ allocation.name }}
                    </p>
                    <span
                      class="rounded-full bg-blue-100 px-2.5 py-1 text-sm font-bold text-blue-700 dark:bg-blue-950 dark:text-blue-200"
                    >
                      +{{ allocation.leads_to_receive }}
                    </span>
                  </div>
                  <div
                    class="mt-3 grid grid-cols-3 gap-2 text-center text-xs text-gray-500"
                  >
                    <div>
                      <p>{{ $t("current") }}</p>
                      <strong class="text-base text-gray-900 dark:text-white">{{
                        allocation.current_leads
                      }}</strong>
                    </div>
                    <div>
                      <p>{{ $t("receiving") }}</p>
                      <strong class="text-base text-blue-600">{{
                        allocation.leads_to_receive
                      }}</strong>
                    </div>
                    <div>
                      <p>{{ $t("projected") }}</p>
                      <strong class="text-base text-emerald-600">{{
                        allocation.projected_leads
                      }}</strong>
                    </div>
                  </div>
                </div>
                <p
                  v-if="leadReassignmentPreview.allocations.length === 0"
                  class="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
                >
                  {{ $t("no_available_moderators") }}
                </p>
              </div>
            </section>
          </div>
        </template>

        <div class="mt-6 flex justify-end gap-3">
          <BaseButton
            type="secondary"
            :disabled="leadReassignmentSaving"
            @click="closeLeadReassignment"
          >
            {{ $t("cancel") }}
          </BaseButton>
          <BaseButton
            type="primary"
            :disabled="!canReassignLeads || leadReassignmentSaving"
            @click="reassignLeads"
          >
            {{
              leadReassignmentSaving
                ? $t("loading")
                : $t("confirm_reassignment")
            }}
          </BaseButton>
        </div>
      </div>
    </div>

    <div
      v-if="showUserReassignment"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="closeUserReassignment"
    >
      <div
        class="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-dark-surface"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-xl font-bold text-gray-900 dark:text-white">
              {{ $t("reassign_moderator_users") }}
            </h2>
            <p class="mt-1 text-sm text-gray-500 dark:text-slate-400">
              {{ $t("user_reassignment_description") }}
            </p>
          </div>
          <button
            class="text-2xl text-gray-400 hover:text-gray-700"
            :aria-label="$t('close')"
            @click="closeUserReassignment"
          >
            ×
          </button>
        </div>

        <div
          v-if="userReassignmentLoading"
          class="py-12 text-center text-gray-500"
        >
          {{ $t("loading") }}
        </div>

        <template v-else-if="userReassignmentPreview">
          <div class="mt-6 grid gap-4 sm:grid-cols-2">
            <div class="rounded-lg bg-violet-50 p-4 dark:bg-violet-950/30">
              <p
                class="text-sm font-medium text-violet-700 dark:text-violet-300"
              >
                {{ $t("users_to_reassign") }}
              </p>
              <p
                class="mt-1 text-3xl font-bold text-violet-900 dark:text-violet-100"
              >
                {{ userReassignmentPreview.user_count }}
              </p>
              <p class="mt-1 text-sm text-violet-700 dark:text-violet-300">
                {{ userReassignmentPreview.source.name }}
              </p>
            </div>
            <div class="rounded-lg bg-emerald-50 p-4 dark:bg-emerald-950/30">
              <p
                class="text-sm font-medium text-emerald-700 dark:text-emerald-300"
              >
                {{ $t("available_moderators") }}
              </p>
              <p
                class="mt-1 text-3xl font-bold text-emerald-900 dark:text-emerald-100"
              >
                {{ userReassignmentPreview.allocations.length }}
              </p>
              <p class="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
                {{ $t("balanced_by_current_user_load") }}
              </p>
            </div>
          </div>

          <div class="mt-6 grid gap-6 lg:grid-cols-2">
            <section>
              <h3 class="font-semibold text-gray-900 dark:text-white">
                {{ $t("assigned_users") }}
              </h3>
              <div
                class="mt-3 max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-slate-700"
              >
                <div
                  v-for="user in userReassignmentPreview.users"
                  :key="user.id"
                  class="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-slate-800"
                >
                  <div>
                    <p class="font-medium text-gray-900 dark:text-white">
                      {{ user.name || "—" }}
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ user.email || user.phone || "—" }}
                    </p>
                  </div>
                  <span
                    class="rounded-full px-2 py-1 text-xs font-medium"
                    :class="
                      user.is_active
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-100 text-gray-600'
                    "
                  >
                    {{ $t(user.is_active ? "active" : "inactive") }}
                  </span>
                </div>
                <p
                  v-if="userReassignmentPreview.users.length === 0"
                  class="p-4 text-sm text-gray-500"
                >
                  {{ $t("no_users_to_reassign") }}
                </p>
              </div>
            </section>

            <section>
              <h3 class="font-semibold text-gray-900 dark:text-white">
                {{ $t("distribution_preview") }}
              </h3>
              <div class="mt-3 space-y-3">
                <div
                  v-for="allocation in userReassignmentPreview.allocations"
                  :key="allocation.profile_id"
                  class="rounded-lg border border-gray-200 p-4 dark:border-slate-700"
                >
                  <div class="flex items-center justify-between gap-3">
                    <p class="font-semibold text-gray-900 dark:text-white">
                      {{ allocation.name }}
                    </p>
                    <span
                      class="rounded-full bg-violet-100 px-2.5 py-1 text-sm font-bold text-violet-700 dark:bg-violet-950 dark:text-violet-200"
                    >
                      +{{ allocation.users_to_receive }}
                    </span>
                  </div>
                  <div
                    class="mt-3 grid grid-cols-3 gap-2 text-center text-xs text-gray-500"
                  >
                    <div>
                      <p>{{ $t("current") }}</p>
                      <strong class="text-base text-gray-900 dark:text-white">{{
                        allocation.current_users
                      }}</strong>
                    </div>
                    <div>
                      <p>{{ $t("receiving") }}</p>
                      <strong class="text-base text-violet-600">{{
                        allocation.users_to_receive
                      }}</strong>
                    </div>
                    <div>
                      <p>{{ $t("projected") }}</p>
                      <strong class="text-base text-emerald-600">{{
                        allocation.projected_users
                      }}</strong>
                    </div>
                  </div>
                </div>
                <p
                  v-if="userReassignmentPreview.allocations.length === 0"
                  class="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
                >
                  {{ $t("no_available_moderators") }}
                </p>
              </div>
            </section>
          </div>
        </template>

        <div class="mt-6 flex justify-end gap-3">
          <BaseButton
            type="secondary"
            :disabled="userReassignmentSaving"
            @click="closeUserReassignment"
          >
            {{ $t("cancel") }}
          </BaseButton>
          <BaseButton
            type="primary"
            :disabled="!canReassignUsers || userReassignmentSaving"
            @click="reassignUsers"
          >
            {{
              userReassignmentSaving
                ? $t("loading")
                : $t("confirm_reassignment")
            }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/components/Button.vue";
import BaseDataTable from "@/components/BaseDataTable.vue";
import BasePagination from "@/components/BasePagination.vue";
import Swal from "sweetalert2";
import activityLogService from "@/services/activityLog.service";
import userService from "@/services/user.service";
import authService from "@/services/auth.service";

export default {
  name: "ModeratorDetail",
  components: { BaseButton, BaseDataTable, BasePagination },
  data() {
    return {
      loading: false,
      isRecentActivityVisible: false,
      moderator: null,
      showLeadReassignment: false,
      leadReassignmentPreview: null,
      leadReassignmentLoading: false,
      leadReassignmentSaving: false,
      showUserReassignment: false,
      userReassignmentPreview: null,
      userReassignmentLoading: false,
      userReassignmentSaving: false,
      statistics: null,
      assignedUsersCount: 0,
      activities: [],
      pagination: null,
      actions: [
        { value: "create_car", label: "Create Car" },
        { value: "update_car", label: "Update Car" },
        { value: "delete_car", label: "Delete Car" },
        { value: "approve_car", label: "Approve Car" },
        { value: "reject_car", label: "Reject Car" },
        { value: "create_user", label: "Create User" },
        { value: "update_user", label: "Update User" },
        { value: "delete_user", label: "Delete User" },
        { value: "login", label: "Login" },
        { value: "logout", label: "Logout" },
      ],
      columns: [],
      filters: {
        action: "",
        period: "",
        from: "",
        to: "",
        page: 1,
        per_page: 10,
      },
    };
  },
  watch: {
    "$i18n.locale": {
      immediate: true,
      handler() {
        this.updateColumns();
      },
    },
    "$route.params.id": {
      handler() {
        this.fetchData();
      },
    },
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  async mounted() {
    await this.fetchData();
  },
  computed: {
    isAdmin() {
      return (authService.getUser()?.roles || []).includes("admin");
    },
    canReassignLeads() {
      return (
        this.leadReassignmentPreview?.lead_count > 0 &&
        this.leadReassignmentPreview?.allocations.length > 0
      );
    },
    canReassignUsers() {
      return (
        this.userReassignmentPreview?.user_count > 0 &&
        this.userReassignmentPreview?.allocations.length > 0
      );
    },
  },
  methods: {
    toggleRecentActivity() {
      this.isRecentActivityVisible = !this.isRecentActivityVisible;
    },
    updateColumns() {
      this.columns = [
        { label: this.$t("date"), field: "created_at" },
        { label: this.$t("action"), field: "action" },
        { label: this.$t("subject"), field: "subject" },
        { label: this.$t("context"), field: "context" },
        { label: this.$t("actions"), field: "actions" },
      ];
    },
    contextPreview(context) {
      if (!context) return "-";
      if (typeof context === "object") return JSON.stringify(context);
      return context;
    },
    openDetails(activity) {
      Swal.fire({
        title: this.$t("activity_details") || "Activity Details",
        html: `
          <div class="text-left space-y-2">
            <p><strong>ID:</strong> ${activity.id}</p>
            <p><strong>${this.$t("date") || "Date"}:</strong> ${
          activity.created_at
        }</p>
            <p><strong>${this.$t("moderator") || "User"}:</strong> ${
          activity.user?.full_name_en || activity.user?.username || "-"
        }</p>
            <p><strong>${
              this.$t("action") || "Action"
            }:</strong> ${this.formatAction(activity.action)}</p>
            <p><strong>${
              this.$t("subject") || "Subject"
            }:</strong> ${this.formatSubject(activity.subject_type)} (ID: ${
          activity.subject_id || "-"
        })</p>
            <p><strong>${
              this.$t("context") || "Context"
            }:</strong> ${this.contextPreview(activity.context)}</p>
            ${
              activity.old_values
                ? `<p><strong>Old Values:</strong> <pre class="text-sm">${JSON.stringify(
                    activity.old_values,
                    null,
                    2
                  )}</pre></p>`
                : ""
            }
            ${
              activity.new_values
                ? `<p><strong>New Values:</strong> <pre class="text-sm">${JSON.stringify(
                    activity.new_values,
                    null,
                    2
                  )}</pre></p>`
                : ""
            }
            <p><strong>IP Address:</strong> ${activity.ip_address || "-"}</p>
            <p><strong>User Agent:</strong> ${activity.user_agent || "-"}</p>
          </div>
        `,
        confirmButtonText: this.$t("close") || "Close",
        confirmButtonColor: "#3085d6",
      });
    },
    async fetchData() {
      await Promise.all([
        this.fetchStatistics(),
        this.fetchAssignedUsersCount(),
        this.fetchActivities(),
        this.isAdmin ? this.fetchModerator() : Promise.resolve(),
      ]);
    },
    async fetchModerator() {
      try {
        const res = await activityLogService.getModerator(this.id);
        this.moderator = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },
    async toggleModeratorActive() {
      const isBlocking = this.moderator.is_active;
      const result = await Swal.fire({
        title: this.$t(isBlocking ? "block_moderator" : "unblock_moderator"),
        text: this.$t(
          isBlocking
            ? "block_moderator_confirmation"
            : "unblock_moderator_confirmation"
        ),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t(isBlocking ? "block" : "unblock"),
        cancelButtonText: this.$t("cancel"),
        confirmButtonColor: isBlocking ? "#dc2626" : "#2563eb",
      });

      if (!result.isConfirmed) return;

      try {
        const res = await activityLogService.toggleModeratorActive(this.id);
        this.moderator = res.data.data;
        await Swal.fire(
          this.$t("success"),
          this.$t("moderator_status_updated"),
          "success"
        );
      } catch (e) {
        Swal.fire(
          this.$t("error"),
          e.response?.data?.message ||
            this.$t("moderator_status_update_failed"),
          "error"
        );
      }
    },
    async changeModeratorPassword() {
      const result = await Swal.fire({
        title: this.$t("change_moderator_password"),
        html: `
          <input id="moderator-password" type="password" class="swal2-input" placeholder="${this.$t(
            "new_password"
          )}">
          <input id="moderator-password-confirmation" type="password" class="swal2-input" placeholder="${this.$t(
            "confirm_password"
          )}">
        `,
        showCancelButton: true,
        confirmButtonText: this.$t("save"),
        cancelButtonText: this.$t("cancel"),
        preConfirm: () => {
          const password = document.getElementById("moderator-password").value;
          const passwordConfirmation = document.getElementById(
            "moderator-password-confirmation"
          ).value;

          if (password.length < 8) {
            Swal.showValidationMessage(this.$t("password_min_length"));
            return false;
          }

          if (password !== passwordConfirmation) {
            Swal.showValidationMessage(this.$t("passwords_do_not_match"));
            return false;
          }

          return password;
        },
      });

      if (!result.isConfirmed) return;

      try {
        await activityLogService.updateModeratorPassword(this.id, {
          password: result.value,
          password_confirmation: result.value,
        });
        Swal.fire(
          this.$t("success"),
          this.$t("moderator_password_updated"),
          "success"
        );
      } catch (e) {
        Swal.fire(
          this.$t("error"),
          e.response?.data?.message ||
            this.$t("moderator_password_update_failed"),
          "error"
        );
      }
    },
    async deleteModerator() {
      const result = await Swal.fire({
        title: this.$t("delete_moderator"),
        text: this.$t("delete_moderator_confirmation"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("delete"),
        cancelButtonText: this.$t("cancel"),
        confirmButtonColor: "#dc2626",
      });

      if (!result.isConfirmed) return;

      try {
        await activityLogService.deleteModerator(this.id);
        await Swal.fire(
          this.$t("success"),
          this.$t("moderator_deleted"),
          "success"
        );
        this.$router.push({ name: "ModeratorsList" });
      } catch (e) {
        Swal.fire(
          this.$t("error"),
          e.response?.data?.message || this.$t("moderator_delete_failed"),
          "error"
        );
      }
    },
    async openLeadReassignment() {
      this.showLeadReassignment = true;
      this.leadReassignmentLoading = true;
      this.leadReassignmentPreview = null;

      try {
        const res =
          await activityLogService.getModeratorLeadReassignmentPreview(this.id);
        this.leadReassignmentPreview = res.data.data;
      } catch (e) {
        this.showLeadReassignment = false;
        Swal.fire(
          this.$t("error"),
          e.response?.data?.message ||
            this.$t("lead_reassignment_preview_failed"),
          "error"
        );
      } finally {
        this.leadReassignmentLoading = false;
      }
    },
    closeLeadReassignment() {
      if (this.leadReassignmentSaving) return;
      this.showLeadReassignment = false;
      this.leadReassignmentPreview = null;
    },
    async reassignLeads() {
      if (!this.canReassignLeads) return;

      this.leadReassignmentSaving = true;
      try {
        const res = await activityLogService.reassignModeratorLeads(this.id);
        this.leadReassignmentPreview = res.data.data;
        this.leadReassignmentSaving = false;
        this.closeLeadReassignment();
        await Swal.fire(
          this.$t("success"),
          this.$t("leads_reassigned_successfully"),
          "success"
        );
      } catch (e) {
        Swal.fire(
          this.$t("error"),
          e.response?.data?.message || this.$t("lead_reassignment_failed"),
          "error"
        );
      } finally {
        this.leadReassignmentSaving = false;
      }
    },
    async openUserReassignment() {
      this.showUserReassignment = true;
      this.userReassignmentLoading = true;
      this.userReassignmentPreview = null;

      try {
        const res =
          await activityLogService.getModeratorUserReassignmentPreview(this.id);
        this.userReassignmentPreview = res.data.data;
      } catch (e) {
        this.showUserReassignment = false;
        Swal.fire(
          this.$t("error"),
          e.response?.data?.message ||
            this.$t("user_reassignment_preview_failed"),
          "error"
        );
      } finally {
        this.userReassignmentLoading = false;
      }
    },
    closeUserReassignment() {
      if (this.userReassignmentSaving) return;
      this.showUserReassignment = false;
      this.userReassignmentPreview = null;
    },
    async reassignUsers() {
      if (!this.canReassignUsers) return;

      this.userReassignmentSaving = true;
      try {
        const res = await activityLogService.reassignModeratorUsers(this.id);
        this.userReassignmentPreview = res.data.data;
        this.userReassignmentSaving = false;
        this.closeUserReassignment();
        await Swal.fire(
          this.$t("success"),
          this.$t("users_reassigned_successfully"),
          "success"
        );
        await this.fetchAssignedUsersCount();
      } catch (e) {
        Swal.fire(
          this.$t("error"),
          e.response?.data?.message || this.$t("user_reassignment_failed"),
          "error"
        );
      } finally {
        this.userReassignmentSaving = false;
      }
    },
    formatLeadStatus(status) {
      if (!status) return "—";
      return status
        .replaceAll("_", " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    async fetchStatistics() {
      try {
        const res = await activityLogService.getModeratorStatistics(this.id);
        this.statistics = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchAssignedUsersCount() {
      try {
        const res = await userService.getUsers({
          moderator_id: this.id,
          per_page: 1,
        });
        this.assignedUsersCount = res.data.pagination?.total ?? 0;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchActivities() {
      this.loading = true;
      try {
        const params = { ...this.filters };
        Object.keys(params).forEach((key) => {
          if (
            params[key] === "" ||
            params[key] === null ||
            params[key] === undefined
          ) {
            delete params[key];
          }
        });
        const res = await activityLogService.getModeratorActivities(
          this.id,
          params
        );
        if (res.data.pagination) {
          this.activities = res.data.data;
          this.pagination = res.data.pagination;
        } else {
          this.activities = res.data.data;
          this.pagination = null;
        }
      } catch (e) {
        Swal.fire(
          this.$t("error"),
          "Failed to fetch moderator activities",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      this.filters.page = page;
      this.fetchActivities();
    },
    periodChanged() {
      if (this.filters.period !== "custom") {
        this.filters.from = "";
        this.filters.to = "";
      }
      this.fetchActivities();
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleString();
    },
    formatAction(action) {
      return action
        .replaceAll("_", " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    },
    formatSubject(subject) {
      if (!subject) return "-";
      return subject.split("\\").pop();
    },
  },
};
</script>
